import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MuiAlert from "@material-ui/lab/Alert";

import { useDialogStyles } from "./styles";
import { ReportResultDialogProps } from "../../../interface/dialog/ReportResultDialogProps";
import { ReportRunFact } from "../../../interface/ReportRunFact";
import { ReportRunStatus } from "../../../interface/ReportRunStatus";
import { ReportRunTone } from "../../../interface/ReportRunTone";
import { StatusColors } from "../../../utils/constants";

const toneColors: Record<ReportRunTone, string | undefined> = {
  default: undefined,
  warning: StatusColors.warning,
  positive: StatusColors.positive,
  negative: StatusColors.negative,
};

const severityFor = (status: ReportRunStatus) => {
  if (status === "error") return "error" as const;
  if (status === "empty") return "warning" as const;
  return "success" as const;
};

// Dense on purpose: a run is read by scanning the numbers against each other, which
// stops working the moment the set no longer fits on one screen. Sizes and weights come
// off the theme's type scale rather than being dialled in here.
const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(0.5, 0, 1.25),
  },
  fact: {
    display: "grid",
  },
  running: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
  },
  detail: {
    marginTop: theme.spacing(4),
  },
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  close: {
    marginRight: theme.spacing(-1),
  },
  // Nothing follows the content now that the footer button is gone.
  content: {
    paddingBottom: theme.spacing(4),
  },
}));

const Fact = ({ fact }: { fact: ReportRunFact }) => {
  const classes = useStyles();

  return (
    <div className={classes.fact}>
      <Typography variant="body2" color="textSecondary">
        {fact.label}
      </Typography>
      <Typography
        variant="body2"
        style={{ color: toneColors[fact.tone ?? "default"] }}
      >
        {fact.value}
      </Typography>
    </div>
  );
};

/**
 * What a report run did, kept on screen until it is dismissed. Reports are generated
 * once a month against numbers nobody can re-derive by eye, so the outcome — what was
 * counted, what was skipped, what was inferred, where the file went — has to survive
 * longer than the toast it used to be reported in.
 */
const ReportResultDialog = ({
  open,
  title,
  result,
  onClose,
  children,
  maxWidth = "sm",
}: ReportResultDialogProps) => {
  const classes = useDialogStyles();
  const styles = useStyles();

  const running = result?.status === "running";

  return (
    <Dialog
      open={open}
      onClose={running ? undefined : onClose}
      // A spinner does not need the width the finished report does.
      maxWidth={running ? "xs" : maxWidth}
      fullWidth
      disableEscapeKeyDown={running}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <div className={styles.title}>
          <Typography variant="h6">{title}</Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={onClose}
            disabled={running}
            className={styles.close}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent
        className={`${classes.dialogContent} ${styles.content}`}
      >
        {running && (
          <div className={styles.running}>
            <CircularProgress size={24} color="primary" />
            <Typography variant="body2">
              {result?.headline ?? "Generating..."}
            </Typography>
          </div>
        )}

        {result && !running && (
          <>
            <MuiAlert severity={severityFor(result.status)} variant="outlined">
              <Typography variant="body2">{result.headline}</Typography>
            </MuiAlert>

            {result.note && (
              <Typography
                variant="body2"
                className={classes.helperText}
                style={{ marginTop: 8 }}
              >
                {result.note}
              </Typography>
            )}

            {(result.sections ?? []).map((section) => (
              <Box key={section.title} className={styles.section}>
                <Typography variant="overline" color="textSecondary">
                  {section.title}
                </Typography>
                <Divider className={styles.divider} />
                <Grid container spacing={2}>
                  {section.facts.map((fact) => (
                    <Grid item xs={6} sm={3} key={fact.label}>
                      <Fact fact={fact} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}

            {children && <div className={styles.detail}>{children}</div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportResultDialog;
